// §12 — search.
//
// "One in-memory index, rebuilt incrementally from four sources: installed apps (id, name,
// keywords), the VFS tree (path + basename; **names only in v1** — content search is v1.1), open
// windows (title + app), and a static settings catalog (id, label, page). Scoring: exact prefix >
// word-boundary subsequence > scattered subsequence, with a recency boost from a per-source MRU.
// Results are grouped by source, best match first, `Enter` launches or focuses. Query and index
// work run in the same tick budget (< 16 ms for 10 000 entries)."
//
// Three things follow from that paragraph and shape everything here:
//
//   • **"Incrementally" is per source.** Installing an app must not cost a walk of ten thousand
//     files, so `setSource` replaces one source's entries and leaves the other three untouched.
//   • **The tiers are an ordering, not a scale.** A prefix match always beats a word-boundary one,
//     which always beats a scattered one; the MRU boost reorders *within* a tier and can never
//     promote a worse kind of match over a better one, because "recently used" is a tie-breaker
//     and not evidence about what was typed.
//   • **Nothing is lowercased at query time.** Case folding ten thousand strings on every
//     keystroke is the whole budget; it is done once, when the source is set.

/**
 * §12's four sources, in the order a launcher lists them when nothing distinguishes their scores.
 *
 * Declared here rather than in `shared/**`: nothing an *app* can see has a search source in it —
 * §4.1 has no `search` group — so this is the kernel's vocabulary and a skin reads it from here.
 */
export const SEARCH_SOURCES = ['app', 'file', 'window', 'setting'] as const;
export type SearchSource = (typeof SEARCH_SOURCES)[number];

/** §12 — the three match tiers, in the order §12 gives them. */
const TIER_EXACT = 1_400;
const TIER_PREFIX = 1_000;
const TIER_WORD = 600;
const TIER_SCATTER = 300;

/**
 * Within a tier: how much of the candidate the query accounted for, plus a little for matching in
 * one run. Bounded well below the gap between tiers, on purpose.
 */
const COVERAGE_WEIGHT = 60;
const CONTIGUOUS_BONUS = 20;

/** §12 — the recency boost. Below `TIER_SCATTER`, so it never crosses a tier. */
const MRU_BOOST = 150;
/** How many ids each source remembers. */
const MRU_LIMIT = 20;

/** Where a word begins: after one of these, or at a lower→upper transition. */
const SEPARATORS = new Set([' ', '-', '_', '/', '.', ':', '\\', '(', '[', ',']);

export interface SearchEntry {
  source: SearchSource;
  /** Stable within its source: an app id, a VFS path, a window id, a setting id. */
  id: string;
  /** What the launcher shows, and the first thing matched against. */
  label: string;
  /**
   * Everything else that should match but is not the label: an app's id and keywords, a file's
   * full path, a window's app name, a setting's page.
   */
  terms?: readonly string[];
  /** One line under the label. Never matched against — it is there to disambiguate, not to find. */
  detail?: string;
}

export interface SearchResult {
  entry: SearchEntry;
  score: number;
}

export interface SearchResultGroup {
  source: SearchSource;
  results: readonly SearchResult[];
}

export interface SearchService {
  /**
   * Replace what one source contributes. The other three are untouched — which is what §12's
   * "rebuilt incrementally" buys: an app install does not re-walk the filesystem.
   */
  setSource(source: SearchSource, entries: readonly SearchEntry[]): void;
  /** §12 — grouped by source, best match first, and the best group first. */
  query(text: string, limit?: number): SearchResultGroup[];
  /** The single best match across every source — what `Enter` acts on. */
  best(text: string): SearchResult | undefined;
  /** §12 — the per-source MRU behind the recency boost. Most recent first, capped. */
  noteUse(source: SearchSource, id: string): void;
  recent(source: SearchSource): readonly string[];
  /** How many entries are indexed, across every source. */
  size(): number;
  subscribe(onChange: () => void): () => void;
}

/**
 * An indexed entry: each searchable string case-folded once, alongside the original.
 *
 * **Nothing is precomputed beyond the case fold.** An earlier cut built a `Set` of word starts per
 * string at index time and blew §12's budget by 7× on a 10 000-entry corpus — twenty thousand set
 * allocations to answer a question that only the *word-boundary tier* ever asks, and that tier is
 * reached only after the scattered test passes and the prefix test fails. It is computed on demand
 * instead, from the original casing, which is also where a camelCase hump can still be seen.
 */
interface Indexed {
  entry: SearchEntry;
  haystacks: Haystack[];
}

interface Haystack {
  /** As written — word boundaries are read off this, so `FlightDeck` still has a hump at `D`. */
  raw: string;
  /** Case-folded once, at index time. Folding on every keystroke is the entire budget. */
  lower: string;
}

/** Whether `raw[i]` begins a word: the start, after a separator, or a lower→upper transition. */
function isWordStart(raw: string, i: number): boolean {
  if (i === 0) return true;
  const previous = raw[i - 1] as string;
  if (SEPARATORS.has(previous)) return true;
  const current = raw[i] as string;
  return (
    previous !== previous.toUpperCase() &&
    current === current.toUpperCase() &&
    current !== current.toLowerCase()
  );
}

function haystack(text: string): Haystack {
  return { raw: text, lower: text.toLowerCase() };
}

/**
 * The cheapest of the three tests, and the superset of all of them: does every character of the
 * query appear, in order? Run first, so an entry that cannot match at any tier costs one pass.
 */
function scattered(hay: string, needle: string): { ok: boolean; contiguous: boolean } {
  let h = 0;
  let last = -2;
  let contiguous = true;
  for (let n = 0; n < needle.length; n += 1) {
    const found = hay.indexOf(needle[n] as string, h);
    if (found === -1) return { ok: false, contiguous: false };
    if (n > 0 && found !== last + 1) contiguous = false;
    last = found;
    h = found + 1;
  }
  return { ok: true, contiguous };
}

/**
 * Every query character lands either at a word start or immediately after the previous match —
 * so `fcs` finds "Flight Control Systems" and `cont` finds "flight **cont**rol".
 *
 * Greedy: it takes the first position that qualifies. A query where a later choice would have
 * matched and the first does not falls through to the scattered tier, which is a *lower* tier
 * anyway — so the greedy shortcut can only ever cost an entry rank, never a result.
 */
function wordBoundary(hay: Haystack, needle: string): boolean {
  const { lower, raw } = hay;
  let from = 0;
  let previous = -2;
  for (let n = 0; n < needle.length; n += 1) {
    const ch = needle[n] as string;
    let found = -1;
    for (let i = from; i < lower.length; i += 1) {
      if (lower[i] !== ch) continue;
      if (i === previous + 1 || isWordStart(raw, i)) {
        found = i;
        break;
      }
    }
    if (found === -1) return false;
    previous = found;
    from = found + 1;
  }
  return true;
}

function scoreAgainst(hay: Haystack, needle: string): number {
  const text = hay.lower;
  const reach = scattered(text, needle);
  if (!reach.ok) return 0;

  const coverage = Math.round((COVERAGE_WEIGHT * needle.length) / Math.max(text.length, 1));
  const contiguity = reach.contiguous ? CONTIGUOUS_BONUS : 0;

  if (text === needle) return TIER_EXACT + COVERAGE_WEIGHT + CONTIGUOUS_BONUS;
  if (text.startsWith(needle)) return TIER_PREFIX + coverage + contiguity;
  if (wordBoundary(hay, needle)) return TIER_WORD + coverage + contiguity;
  return TIER_SCATTER + coverage + contiguity;
}

/** Whether a candidate outranks a result already in the list. Ties break on the label. */
function beats(score: number, label: string, other: SearchResult): boolean {
  if (score !== other.score) return score > other.score;
  return label < other.entry.label;
}

export function createSearchService(): SearchService {
  /** source → its entries. Replaced wholesale per source; never merged across sources. */
  const bySource = new Map<SearchSource, Indexed[]>();
  /** source → recently used ids, most recent first. */
  const mru = new Map<SearchSource, string[]>();
  const listeners = new Set<() => void>();

  const announce = (): void => {
    for (const listener of [...listeners]) listener();
  };

  /** Where `id` sits in its source's MRU, or -1. */
  const recencyOf = (source: SearchSource, id: string): number =>
    mru.get(source)?.indexOf(id) ?? -1;

  function scoreEntry(indexed: Indexed, needle: string): number {
    let best = 0;
    for (const hay of indexed.haystacks) {
      const score = scoreAgainst(hay, needle);
      if (score > best) best = score;
    }
    if (best === 0) return 0;

    // §12 — the recency boost, decaying with position in the MRU. Bounded below one tier, so a
    // recently opened file never outranks a better match on what was actually typed.
    const rank = recencyOf(indexed.entry.source, indexed.entry.id);
    if (rank === -1) return best;
    return best + Math.round((MRU_BOOST * (MRU_LIMIT - rank)) / MRU_LIMIT);
  }

  /** A local function rather than a method, so `best` works when the service is destructured. */
  function query(text: string, limit = 8): SearchResultGroup[] {
    const needle = text.trim().toLowerCase();
    if (needle.length === 0) return [];

    const groups: SearchResultGroup[] = [];
    for (const [source, entries] of bySource) {
      /**
       * The top `limit`, kept sorted as we go rather than collected and sorted at the end.
       *
       * A launcher shows eight rows. Collecting ten thousand matches for `report` and sorting them
       * is work whose only product is thrown away — and it put §12's query over budget by 30 %,
       * most of it inside `localeCompare`. The tie-break is a plain code-unit comparison for the
       * same reason: it is a *tie*-break, so all it has to be is stable and cheap.
       */
      const results: SearchResult[] = [];
      for (const indexed of entries) {
        const score = scoreEntry(indexed, needle);
        if (score === 0) continue;
        const worst = results[results.length - 1];
        if (results.length === limit && worst !== undefined && !beats(score, indexed.entry.label, worst)) {
          continue;
        }
        let at = results.length;
        while (at > 0 && beats(score, indexed.entry.label, results[at - 1] as SearchResult)) at -= 1;
        results.splice(at, 0, { entry: indexed.entry, score });
        if (results.length > limit) results.pop();
      }
      if (results.length === 0) continue;
      groups.push({ source, results });
    }

    // §12 — "grouped by source, best match first". The best *group* leads: which kind of thing the
    // user meant is itself something the scores answer. Ties fall back to `SEARCH_SOURCES` order
    // rather than to whichever source happened to be indexed first.
    groups.sort(
      (a, b) =>
        (b.results[0]?.score ?? 0) - (a.results[0]?.score ?? 0) ||
        SEARCH_SOURCES.indexOf(a.source) - SEARCH_SOURCES.indexOf(b.source),
    );
    return groups;
  }

  return {
    setSource(source, entries) {
      bySource.set(
        source,
        entries.map((entry) => ({
          entry,
          haystacks: [haystack(entry.label), ...(entry.terms ?? []).map(haystack)],
        })),
      );
      announce();
    },

    query,

    best(text) {
      let winner: SearchResult | undefined;
      for (const group of query(text, 1)) {
        const head = group.results[0];
        if (head && (!winner || head.score > winner.score)) winner = head;
      }
      return winner;
    },

    noteUse(source, id) {
      const list = mru.get(source) ?? [];
      const next = [id, ...list.filter((existing) => existing !== id)].slice(0, MRU_LIMIT);
      mru.set(source, next);
    },

    recent(source) {
      return mru.get(source) ?? [];
    },

    size() {
      let total = 0;
      for (const entries of bySource.values()) total += entries.length;
      return total;
    },

    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
  };
}
